import * as React from 'react';
import { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { TabEnum } from '../../../../../../models/enums/tab-enum';
import { useRootStore } from '../../../../../../providers/root-store-provider';
import { SkeletonDashboardProfitChart } from '../../../../../../components/app/skeleton/dashboard/trades/protif-chart/skeleton-dashboard-profit-chart';
import { BaseLineChart } from '../../../../../../components/app/chart/base/line/base-line-chart';
import { NoData } from '../../../../../../components/app/no-data/no-data';
import { UnrealizedProfitTooltipContent } from '../../../../../../components/app/dashboard/return-metric/unrealized-profit-tooltip-content';
import { ReturnMetricEnum } from '../../../../../../models/enums/return-metric-enum';
import { getSeriesIdForMetric, RETURN_METRIC_LABELS } from '../../../../../../models/constants/return-metric';
import { useReturnMetricColors } from '../../../../../../components/app/dashboard/return-metric/dashboard-return-metric';
import { ChartDataEnum, PortfolioTypeEnum } from '../../../../../../defs/api';

type DashboardProfitChartPercentageType = {
    toggleFullScreen: () => void;
    isFullScreen: boolean;
};

export const DashboardTradesProfitChartPercentage: React.FC<DashboardProfitChartPercentageType> = observer(
    ({ toggleFullScreen, isFullScreen }: DashboardProfitChartPercentageType) => {
        const { t } = useTranslation();
        const rootStore = useRootStore();
        const { statisticStore, portfolioStore } = rootStore;
        const { profitStore } = statisticStore;
        const metricColors = useReturnMetricColors();
        const isSummaryPortfolio = portfolioStore.selectedPortfolio.type === PortfolioTypeEnum.Summary;
        const useLegacySummaryMetricLock =
            process.env.NEXT_PUBLIC_DISABLE_SUMMARY_TWR_2038 === 'true' && isSummaryPortfolio;

        const selectedChartData = useMemo(() => {
            const metrics = useLegacySummaryMetricLock
                ? [ReturnMetricEnum.SimpleReturn]
                : [ReturnMetricEnum.SimpleReturn, ReturnMetricEnum.TimeWeightedReturn];
            const portfolioSeriesIds = metrics.map((metric) => getSeriesIdForMetric('profitLoss', metric));
            const benchmarkMetric = useLegacySummaryMetricLock
                ? ReturnMetricEnum.SimpleReturn
                : ReturnMetricEnum.TimeWeightedReturn;
            const benchmarkSeriesIds = ['CSPX', 'CNDX', 'BTC-USD'].map((id) =>
                getSeriesIdForMetric(id, benchmarkMetric),
            );
            const knownMetricSeriesIds = ['profitLoss', 'CSPX', 'CNDX', 'BTC-USD'].flatMap((id) => [
                getSeriesIdForMetric(id, ReturnMetricEnum.SimpleReturn),
                getSeriesIdForMetric(id, ReturnMetricEnum.TimeWeightedReturn),
            ]);
            const additionalComparisonCharts = statisticStore.profitStore.percentageProfits.filter(
                (chart) => !knownMetricSeriesIds.includes(chart.id),
            );

            return [...portfolioSeriesIds, ...benchmarkSeriesIds]
                .map((id) => {
                    const chart = statisticStore.profitStore.percentageProfits.find((item) => item.id === id);

                    return chart ?? null;
                })
                .filter((chart): chart is NonNullable<typeof chart> => {
                    return Boolean(chart) && (chart?.data.length ?? 0) > 0;
                })
                .concat(additionalComparisonCharts);
        }, [statisticStore.profitStore.percentageProfits, useLegacySummaryMetricLock]);

        useEffect(() => {
            profitStore.syncVisibleChartFilter(selectedChartData);
        }, [profitStore, selectedChartData]);

        const legendLabels = useMemo(() => {
            const labels: Record<string, string> = {};

            selectedChartData.forEach((chart) => {
                const metric = chart.id.endsWith('_twr')
                    ? ReturnMetricEnum.TimeWeightedReturn
                    : ReturnMetricEnum.SimpleReturn;
                const baseId = chart.id.replace(/_twr$/, '');

                if (chart.chartDataType === ChartDataEnum.PinnedPortfolioValue) {
                    return;
                }

                labels[chart.id] =
                    baseId === 'profitLoss' ? RETURN_METRIC_LABELS[metric] : t(`pages.index.chart.legend.${baseId}`);
            });

            return labels;
        }, [selectedChartData, t]);

        const legendTooltips = useMemo(
            () => ({
                profitLoss: (
                    <UnrealizedProfitTooltipContent
                        description={t('pages.index.graphPercentageTooltipDescription')}
                        metrics={[ReturnMetricEnum.SimpleReturn]}
                    />
                ),
                profitLoss_twr: (
                    <UnrealizedProfitTooltipContent
                        description={t('pages.index.graphPercentageTooltipDescription')}
                        metrics={[ReturnMetricEnum.TimeWeightedReturn]}
                    />
                ),
            }),
            [t],
        );

        const hasChartData = selectedChartData.some((chart) => chart.data.length > 0);

        if (profitStore.loading || portfolioStore.selectedPortfolio.isRecalculating) {
            return <SkeletonDashboardProfitChart />;
        }

        if (!hasChartData) {
            return <NoData>{t('layout.tables.noDetailedData')}</NoData>;
        }

        return (
            <BaseLineChart
                tab={TabEnum.Percentage}
                dateFormat={statisticStore.chartDateFormat}
                labelDateFormat={process.env.NEXT_PUBLIC_DATE_FORMAT as string}
                chartData={selectedChartData}
                unit="%"
                filter={profitStore.filter}
                onChange={(id) => profitStore.filterSelected(id)}
                seriesLabels={legendLabels}
                colorOverrides={{
                    profitLoss: metricColors[ReturnMetricEnum.SimpleReturn],
                    profitLoss_twr: metricColors[ReturnMetricEnum.TimeWeightedReturn],
                }}
                legendLabels={legendLabels}
                legendTooltips={legendTooltips}
                toggleFullScreen={toggleFullScreen}
                isFullScreen={isFullScreen}
            />
        );
    },
);
